import * as React from 'react';
import withStyles from 'react-jss';
import { StylesProps, Carrier } from 'src/Library/Types';
import { VesselSchedulesStyles } from '../../../VesselSchedules/VesselSchedules.styles';
import { connect } from 'react-redux';
import { SearchedVesselSchedules } from '../../../VesselSchedules/VesselSchedules.types';
import { ChargeListCardsStyles } from '../../../DiscoverRate/ChargeListCards.styles';
import Row from 'antd/lib/row';
import Col from 'antd/lib/col';
import Tooltip from 'antd/lib/tooltip';
import { useGenericState } from 'src/Library/UseGenericState';
import classNames from 'classnames';
import { getDateValue, isValidArray, stops } from 'src/Library/Helper';
import { Ports } from 'src/components/common/Types/CommonTypes';
import { renderLine, renderDot } from 'src/Library/Common';

const styles = (theme) => ({
  ...ChargeListCardsStyles(theme),
  ...VesselSchedulesStyles(theme),
})

interface IProps extends StylesProps<ReturnType<typeof styles>> {
  searchResult: SearchedVesselSchedules;
  isTaskDone: boolean;
  allCarriersMap: {
    key: Carrier;
  };
  allPortsMap: {
    key: Ports;
  };
  extraContent: {
    isServiceNamePresent: boolean;
    isCutOffPresent: boolean;
  };
}

interface StateProps {
  imgDimensions: {
    height: number,
    width: number,
  };
  isViewDetailsVisible: boolean;
};

const VesselSchedulesCards = (props : IProps): React.ReactElement => {
  const [state, setState] = useGenericState<StateProps>({
    imgDimensions: {
      height: 0,
      width: 0,
    },
    isViewDetailsVisible: false,
  });

  const {
    classes,
    searchResult,
    allCarriersMap,
    allPortsMap,
    extraContent,
    isTaskDone,
  } = props;

  const {
    imgDimensions,
    isViewDetailsVisible,
  } = state;

  React.useEffect( () => {
    getImgHeightWidth();
    if (isTaskDone) setState({ isViewDetailsVisible: true });
  }, [allCarriersMap]);

  const getImgHeightWidth = () => {
    const img = new Image();
    img.src = allCarriersMap && allCarriersMap[searchResult?.scac]?.logoUrl;
    const maxWidth = 100;
    const maxHeight = 40;
    img.onload = () => {
      const imgHeight = img.naturalHeight || img.height;
      const imgWidth = img.naturalWidth || img.width;
      const ratio = Math.min(maxWidth / imgWidth, maxHeight / imgHeight);
      const width = imgWidth * ratio;
      const height = imgHeight * ratio;
      setState({ imgDimensions: { width, height } });
    };
  };

  const renderTooltip = (title, content) : React.ReactElement => {
    return (
      <Tooltip
        title={title}
        overlayStyle={{ fontSize: '12px' }}
        mouseEnterDelay={0.5}
        placement="topLeft"
      >
        {content}
      </Tooltip>
    );
  };

  const renderCarrierInfo = () : React.ReactElement => {
    const title = `${allCarriersMap && allCarriersMap[searchResult?.scac]?.name}`;
    const content = (
      <span
        className={classes.carrierCode}
        style={{ fontSize: '12px' }}
      >
        {allCarriersMap && allCarriersMap[searchResult?.scac]?.id}
      </span>
    );
    return (
      <Col span={6} className={classes.carrierInfo}>
        <span style={{ width: '100px', marginRight: '18px', minWidth: '100px' }}>
          <img
            src={allCarriersMap && allCarriersMap[searchResult?.scac]?.logoUrl}
            className={classes.carrierLogo}
            style={{ ...imgDimensions }}
          />
        </span>
        {renderTooltip(title, content)}
      </Col>
    );
  };

  const renderLineAndDots = (transshipmentPorts): React.ReactElement => {
    return <span className={classes.lineAndDot}>
      {renderDot('', classes.dot)}
      {renderLine(classes.line)}
      {isValidArray(transshipmentPorts) ? (
        <React.Fragment>
          {renderDot({
            backgroundColor: '#333333',
            height: '4px',
            width: '4px',
          }, classes.dot)}
          {renderLine(classes.line)}
        </React.Fragment>
       ) : null}
      {renderDot('', classes.dot)}
    </span>;
  };

  const renderNoOfTransshipments = (isDirect, transshipmentPorts) : React.ReactElement => {
    return (
      <div style={{ fontSize: '10px' }}>
        {!isDirect ?
          <React.Fragment>
            <Tooltip title={
              <React.Fragment>
                {transshipmentPorts?.map(item => (
                  <div>
                    <img
                      src={allPortsMap && allPortsMap[item.departureUnloc]?.flagS3Link}
                      style={{ maxWidth: '11px', marginRight: '4px' }}
                    />
                    {allPortsMap && allPortsMap[item.departureUnloc]?.name}
                  </div>)
                )}
              </React.Fragment>}
              overlayClassName={classes.tooltipStyle}
              mouseEnterDelay={0.3}
            >
              <span className={classes.portInfo}>
                {transshipmentPorts.length} Transshipment{transshipmentPorts.length > 1 ? 's' : ''}
              </span>
            </Tooltip>
          </React.Fragment>
        :
        <span>Direct</span>}
      </div>
    );
  };

  const renderTransshipmentPorts = (transshipmentPorts) : React.ReactElement => {
    return (
      <Tooltip
        title={
          <React.Fragment>
            {transshipmentPorts?.map(item => <div>
                <img
                  src={allPortsMap && allPortsMap[item.departureUnloc]?.flagS3Link}
                  style={{ maxWidth: '11px', marginRight: '4px' }}
                />
                {allPortsMap && allPortsMap[item.departureUnloc]?.name}
              </div>)
            }
          </React.Fragment>
        }
        overlayClassName={classes.tooltipStyle}
        mouseEnterDelay={0.3}
      >
        <span className={classes.portInfo}>
          {allPortsMap && allPortsMap[transshipmentPorts[0]?.departureUnloc]?.name}
          {transshipmentPorts.length > 1 && `, + ${transshipmentPorts.length - 1}`}
        </span>
      </Tooltip>
    )
  }

  const renderRoute = () : React.ReactElement => {
    const isDirect = isDirectShipment();
    const transshipmentPorts = !isDirect ? searchResult?.legs?.filter(port => port.transshipmentIndicator) : []
    return (
      <div className={classes.route} style={{ width: '38%', lineHeight: 2 }}>
        {renderNoOfTransshipments(isDirect, transshipmentPorts)}
        {renderLineAndDots(transshipmentPorts)}
        {renderTransshipmentPorts(transshipmentPorts)}
      </div>
    );
  };

  const renderRouteInfo = () : React.ReactElement => {
    return (
      <Col span={15}
        className={classNames(classes.flexDiv, classes.border)}
        style={{ alignItems: extraContent.isCutOffPresent ? 'unset' : 'center' }}
      >
        {renderDate(
          searchResult?.originDepartureDate,
          searchResult?.originUnloc,
          searchResult?.originCityName,
          true,
        )}
        {renderRoute()}
        {renderDate(
          searchResult?.destinationArrivalDate,
          searchResult?.destinationUnloc,
          searchResult?.destinationCityName,
        )}
      </Col>
    );
  };

  const getDateYearDay = (value) => {
    const date = getDateValue(value)?.format('DD MMM');
    const year = getDateValue(value)?.format('YYYY');
    const day = getDateValue(value)?.format('ddd');

    return ({
      date,
      year,
      day,
    });
  };

  const renderDate = (value, port, cityName, showCutOffDate = false) : React.ReactElement => {
    const date = getDateYearDay(value);
    const cutOffDate = getDateYearDay(searchResult?.terminalCutoff);
    return (
      <div style={{ margin: '0px 15px', lineHeight: 2 }}>
        <div className={classes.date}>
          <span style={{ fontSize: '16px' }}>{date?.date} </span>
          <span style={{ fontSize: '12px' }}>{date?.year} </span>
          <span style={{ fontSize: '12px', fontWeight: 'normal' }}>({date?.day})</span>
        </div>
        {<div className={classes.portInfo}>
          {allPortsMap && allPortsMap[port]?.code ?
            <Tooltip
              title={allPortsMap && allPortsMap[port]?.name}
              overlayStyle={{ fontSize: '12px' }}
            >
              <img src={allPortsMap && allPortsMap[port]?.flagS3Link} style={{ maxWidth: '10px' }} />
              <span style={{ marginLeft: '5px' }}>{allPortsMap && allPortsMap[port]?.code}</span>
            </Tooltip> :
            <Tooltip
              title={cityName}
              overlayStyle={{ fontSize: '12px' }}
            >
              {port}
            </Tooltip>
          }
        </div>}
        {showCutOffDate && extraContent.isCutOffPresent && <div className={classes.cutOffDate}>
          Cut off : {searchResult?.terminalCutoff ?
            <span>{cutOffDate?.date}
              <span style={{ fontSize: '8px', fontWeight: 'normal' }}> {cutOffDate?.year}</span>
            </span> :
          <span style={{ color: '#CCCCCC', fontSize: '9px', fontWeight: 'normal' }}>Not Available</span>
        }
        </div>}
      </div>
    );
  };

  const renderTransitDays = () : React.ReactElement => {
    return (
      <Col span={3} className={classes.transitDays} style={{ lineHeight: 2 }}>
        <span
          style={{
            fontSize: '21px',
            color: '#333333',
            fontWeight: 'bold',
            // marginBottom: -15,
          }}
        >
          {searchResult?.totalDuration}
        </span>
        <span style={{ fontSize: '10px', color: '#666666' }}>
          Transit Day{searchResult?.totalDuration > 1 ? 's' : ''}
        </span>
      </Col>
    );
  };

  const isDirectShipment = () => {
    return !isValidArray(searchResult?.legs) || !(searchResult?.legs?.some(port => port.transshipmentIndicator)); 
  }

  const getBottomColor = () => {
    return 'rgba(255, 245, 219, 0.7)';
  };

  const handleViewDetailsClick = () : void => {
    setState({ isViewDetailsVisible: !isViewDetailsVisible });
  };

  const renderBottomDivContent = () : React.ReactElement => {
    const content = `Vessel : ${searchResult?.vesselName}
      ${searchResult?.voyageNumber ? ` | ${searchResult?.voyageNumber}` : ''}`
    return (
      <Row
        className={classes.bottomDiv}
        onClick={(e) => {
          stops(e);
          !isTaskDone ? handleViewDetailsClick() : null;
        }}
        style={{
          backgroundColor: getBottomColor(),
          border: '0 0 3px 3px',
          cursor: !isTaskDone ? 'pointer' : 'default',
        }}
      >
        <Col span={6} className={classes.bottomContent} style={{ paddingLeft: '15px' }}>
          {renderTooltip(content, content)}
        </Col>
        <Col span={13} className={classes.bottomContent} style={{ paddingLeft: '10px' }}>
          {extraContent.isServiceNamePresent && <React.Fragment>
            Service : {searchResult?.serviceName ?
            searchResult?.serviceName :
            <span style={{ color: '#CCCCCC', fontSize: '9px' }}>Not Available</span>}
          </React.Fragment>}
        </Col>
        <Col span={5} className={classes.bottomContent}>
          <span style={{ color: '#006EC3', float: 'right', paddingRight: '15px' }}>
            {!isTaskDone && (isViewDetailsVisible ? 'Hide Details' : 'View Details')}
          </span>
        </Col>
      </Row>
    );
  };

  const renderScheduleBreakdown = () : React.ReactElement => {
    return (
      <div className={classes.chargesDiv}>
        <Row>
          <Col span={6} style={{ padding: '10px', paddingLeft: '16px'  }}>Vessel Voyage</Col>
          <Col span={5} style={{ padding: '10px' }}>Service</Col>
          <Col span={5} style={{ padding: '10px' }}>Departure</Col>
          <Col span={3} className={classes.unitsNo}>Transit Days</Col>
          <Col span={5} style={{ padding: '10px' }}>Arrival</Col>
        </Row>
        <div>{renderScheduleDetails()}</div>
      </div>
    );
  };

  const renderTransshipmentDetails = (value, cityName, port, terminal) : React.ReactElement => {
    const date = getDateYearDay(value);
    return (
      <React.Fragment>
        <div style={{ fontSize: '10px' }}>
          <span style={{ fontSize: '12px', fontWeight: 600 }}>{date?.date} </span>
          <span>{date?.year}, {date?.day}</span>
        </div>
        <div className={classes.ellipsis}>
          <img src={allPortsMap && allPortsMap[port]?.flagS3Link} style={{ maxWidth: '10px', marginRight: '4px' }}/>
          {renderTooltip(cityName, cityName)}
        </div>
        <div className={classes.terminal}>{terminal}</div>
      </React.Fragment>
    );
  };

  const getVesselVoyage = (item) : string => {
    if (item.transportName) {
      return `${item.transportName} ${item.conveyanceNumber ? ` | ${item.conveyanceNumber}` : ''}`;
    }
    if (item.conveyanceNumber) {
      return item.conveyanceNumber;
    }
    return '-';
  };

  const renderSchedule = (item, index) => {
    let color = '#FFFFFF';
    if (index % 2 === 0) color = '#F9F9F9';
    const height = item.arrivalTerminal || item.departureTerminal ? '80px' : '52px';
    const styles = {
      height, backgroundColor: color, padding: '9px 10px', color: '#333333',
    };
    const vesselVoyage = getVesselVoyage(item);
    return (
      <React.Fragment>
        <Col span={6} style={{ height, backgroundColor: color, color: '#333333' }}
          className={classNames(classes.chargeName, classes.ellipsis)}>
          {item.transportName || item.conveyanceNumber ?
            renderTooltip(vesselVoyage, vesselVoyage) : '-'}
        </Col>
        <Col span={5} style={{ ...styles }} className={classes.ellipsis}>
          {item.serviceName ?
              renderTooltip(item.serviceName, item.serviceName) : '-'}
        </Col>
        <Col span={5} style={{ ...styles }}>
          {renderTransshipmentDetails(
            item.departureDate,
            item.departureCityName,
            item.departureUnloc,
            item.departureTerminal,
          )}
        </Col>
          <Col span={3} style={{ ...styles, textAlign: 'center' }}>
          {item.transitDuration ?? '-'}
        </Col>
        <Col span={5} style={{ ...styles }}>
          {renderTransshipmentDetails(
            item.arrivalDate,
            item.arrivalCityName,
            item.arrivalUnloc,
            item.arrivalTerminal,
          )}
        </Col>
      </React.Fragment>
    );
  };

  const renderScheduleDetails = () : React.ReactElement => {
    if (!isValidArray(searchResult?.legs) && searchResult) {
      const {
        originDepartureDate,
        originCityName,
        originUnloc,
        originTerminal,
        destinationTerminal,
        destinationUnloc,
        destinationCityName,
        destinationArrivalDate,
        voyageNumber,
        vesselName,
        serviceName,
        totalDuration,
      } = searchResult;
      const color = '#F9F9F9';
      const height = originTerminal || destinationTerminal ? '80px' : '52px';
      const vesselVoyage = `${vesselName} ${voyageNumber ? ` | ${voyageNumber}` : ''}`;
      const styles = {
        height, backgroundColor: color, padding: '9px 10px', color: '#333333',
      };
      return (
        <Row style={{ display: 'flex' , alignItems: 'center', lineHeight: 1 }}>
          <Col span={6} style={{ height, backgroundColor: color, color: '#333333' }}
            className={classNames(classes.chargeName, classes.ellipsis)}>
            {vesselName || voyageNumber ? renderTooltip(vesselVoyage, vesselVoyage) : '-'}
          </Col>
          <Col span={5} style={{ ...styles }} className={classes.ellipsis}>
            {searchResult?.serviceName ?
                renderTooltip(serviceName, serviceName) : '-'}
          </Col>
          <Col span={5} style={{ ...styles }}>
            {renderTransshipmentDetails(
              originDepartureDate,
              originCityName,
              originUnloc,
              originTerminal,
            )}
          </Col>
            <Col span={3} style={{ ...styles, textAlign: 'center' }}>
            {totalDuration ?? '-'}
          </Col>
          <Col span={5} style={{ ...styles }}>
            {renderTransshipmentDetails(
              destinationArrivalDate,
              destinationCityName,
              destinationUnloc,
              destinationTerminal,
            )}
          </Col>
        </Row>
      )
    }

    return (
      <React.Fragment>
        {
          searchResult?.legs?.map((item, index) => (
            <Row style={{ display: 'flex' , alignItems: 'center', lineHeight: 1 }}>
              {renderSchedule(item, index)}
            </Row>
          ))
        }
      </React.Fragment>
    );
  };

  return (
    <div className={classes.cardDiv} style={{ marginBottom: '9px' }}>
      <Row className={classes.rateCard}>
        {renderCarrierInfo()}
        {renderRouteInfo()}
        {renderTransitDays()}
      </Row>
      {isViewDetailsVisible && renderScheduleBreakdown()}
      {renderBottomDivContent()}
    </div>
  );
};

export default (React.memo(withStyles(styles)(VesselSchedulesCards)));
